import { useCallback, useEffect, useRef, useState } from "react";
import { comboFromEvent } from "./keyNames.ts";
import { setSequencesSuspended } from "./sequences.ts";

/**
 * How long after a bare key the recorder waits for a second one before
 * committing the single key. Long enough to type `g` then `c` deliberately,
 * short enough that a single key does not feel stuck.
 */
const SEQUENCE_PAUSE_MS = 1000;

/**
 * Records one key combo from the user: a chord (`alt+e`, `mod+shift+k`), a
 * bare key (`t`), or a two-key sequence (`g c`).
 *
 * While recording, a capture-phase listener on `window` swallows every key so
 * the app's own chord shortcuts cannot fire from the keys being recorded
 * (`mod+k` would otherwise open the agent); the sequence layer listens on the
 * same node, where stopping propagation does not reach it, so it is suspended
 * for the duration instead. A chord commits at once. A bare key waits
 * for a second bare key, which makes a sequence; a pause, a click elsewhere,
 * or the window losing focus commits what was captured. Escape cancels, so it
 * is the one key that cannot be recorded.
 */
export const useShortcutRecorder = (onCommit: (combo: string) => void) => {
  // `null` when idle; the bare keys captured so far while recording.
  const [steps, setSteps] = useState<string[] | null>(null);
  const stepsRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const teardownRef = useRef<(() => void) | undefined>(undefined);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const stop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = undefined;
    teardownRef.current?.();
    teardownRef.current = undefined;
    stepsRef.current = [];
    setSequencesSuspended(false);
    setSteps(null);
  }, []);

  const start = useCallback(() => {
    stop();
    stepsRef.current = [];
    setSequencesSuspended(true);
    setSteps([]);

    const commit = (combo: string) => {
      stop();
      onCommitRef.current(combo);
    };
    const commitPending = () => {
      const pending = stepsRef.current;
      if (pending.length > 0) commit(pending.join(" "));
      else stop();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      if (event.key === "Escape" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        stop();
        return;
      }
      const combo = comboFromEvent(event);
      if (combo === null) return;

      if (combo.includes("+")) {
        // A chord cannot be a step of a sequence; one pressed mid-sequence is
        // ignored rather than silently replacing what was captured.
        if (stepsRef.current.length === 0) commit(combo);
        return;
      }

      const next = [...stepsRef.current, combo];
      stepsRef.current = next;
      setSteps(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (next.length >= 2) {
        commit(next.join(" "));
        return;
      }
      timerRef.current = setTimeout(commitPending, SEQUENCE_PAUSE_MS);
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", commitPending, true);
    window.addEventListener("blur", commitPending);
    teardownRef.current = () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", commitPending, true);
      window.removeEventListener("blur", commitPending);
    };
  }, [stop]);

  // A row that unmounts mid-recording must not leave the listener behind.
  useEffect(() => stop, [stop]);

  return { recording: steps !== null, steps: steps ?? [], start, cancel: stop };
};
